import {
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
  Button as BootstrapButton,
} from "react-bootstrap";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import clinicIcon from "../../media/clinics.png";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import EditableModal from "../../components/layout/editableModal/EditableModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import filterIcon from "../../media/filter_icon.png";

function Contacts() {
  const { t } = useTranslation("contacts");
  const [contacts, setContacts] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [filters, setFilters] = useState({ region: "all" });
  const [isFiltersActive, setIsFiltersActive] = useState(false);

  // === Модальное окно (добавление) ===
  const [showModal, setShowModal] = useState(false);
  const [newCity, setNewCity] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    time: "",
    description: "",
    region: "",
  });

  // === Модальное окно (редактирование клиники) ===
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [clinicInitialData, setClinicInitialData] = useState({});

  // Поля формы редактирования клиники (аналогично услуге)
  const clinicFields = useMemo(
    () => [
      { name: "name", label: t("fields.name"), required: true, bold: true },
      {
        name: "region",
        label: t("fields.city"),
        type: "select",
        options: Object.entries(citiesMap).map(([cityName, cityId]) => ({
          value: String(cityId),
          label: cityName,
        })),
      },
      { name: "phone", label: t("fields.phone") },
      { name: "email", label: t("fields.email") },
      { name: "address", label: t("fields.address") },
      { name: "time", label: t("fields.time") },
      {
        name: "description",
        label: t("fields.description"),
        type: "textarea",
        rows: 3,
      },
    ],
    [citiesMap, t]
  );

  const loadContacts = useCallback(async () => {
    const preserveSearchFocus = captureSearchFocus();
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "contacts",
        block: "list",
        region: filters.region === "all" ? "" : filters.region,
        search: debouncedSearchTerm.trim(),
      });
      console.log("Полученные данные:", data);
      setContacts(data.result || []);
      setCities(data.cities || []);
      setCitiesMap(data.cities_map || {});
    } catch (err) {
      console.error("Ошибка загрузки клиник:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  }, [filters.region, debouncedSearchTerm, captureSearchFocus, restoreSearchFocus]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
      document.title = t("list.title");
  }, [t]);

  const handleSaveCity = async () => {
    try {
      await GetData({
        module: "contacts",
        block: "list",
        action: "add",
        name: newCity.name,
        phone: newCity.phone,
        email: newCity.email,
        address: newCity.address,
        time: newCity.time,
        description: newCity.description,
        region: newCity.region,
      });
      await loadContacts();
      setShowModal(false);
    } catch (err) {
      console.error("Ошибка добавления:", err);
      alert(t("list.errors.addFailed"));
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({ region: "all" });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCity((prev) => ({ ...prev, [name]: value }));
  };

  // === Работа с модальным окном ===
  const handleAddClick = () => {
    // Сбрасываем форму при открытии
    setNewCity({
      name: "",
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingId(null);
  };

  const handleEditClick = async (contact) => {
    const id = contact?.id?.value ?? contact?.id;
    if (!id) return;

    try {
      const data = await GetData({
        module: "contacts",
        block: "info",
        id_contact: id,
      });

      let clinicData = data.result || contact;
      if (Array.isArray(clinicData) && clinicData.length > 0) {
        clinicData = clinicData[0];
      }
      if (typeof clinicData !== "object" || clinicData === null) {
        clinicData = contact;
      }

      setEditingId(Number(id));
      setClinicInitialData({
        name: clinicData.name ?? "",
        phone: clinicData.phone ?? "",
        email: clinicData.email ?? "",
        address: clinicData.address ?? "",
        time: clinicData.time ?? "",
        description: clinicData.description ?? "",
        region:
          citiesMap[clinicData.region] != null
            ? String(citiesMap[clinicData.region])
            : clinicData.region ?? "",
      });
      setShowEditModal(true);
    } catch (err) {
      console.error("Ошибка загрузки клиники:", err);
      alert(t("list.errors.clinicLoadFailed"));
    }
  };

  const handleSaveClinic = async (formData) => {
    try {
      await GetData({
        module: "contacts",
        block: "info",
        id_contact: editingId,
        action: "change",
        data: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          time: formData.time,
          description: formData.description,
          region: formData.region,
        },
      });
      await loadContacts();
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      alert(t("profile.errors.saveFailed"));
      return false;
    }
  };

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={clinicIcon}
                title={t("list.title")}
                showStatus={false}
                status={""}
              />
            }
            buttons={[
              {
                label: t("list.add"),
                onClick: handleAddClick,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              {/* Панель поиска */}
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <label>{t("filters.searchLabel", { ns: "common" })}</label>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <label>{t("list.cityLabel")}</label>
                      <select
                        value={filters.region}
                        onChange={(e) =>
                          handleFilterChange("region", e.target.value)
                        }
                      >
                        <option value="all">{t("list.all")}</option>
                        {cities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="button-default light"
                      onClick={clearFilters}
                    >
                      {t("filters.reset", { ns: "common" })}
                    </button>
                  </div>
                </div>
              </div>
              {/* Список клиник */}
              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : contacts.length === 0 ? (
                  <div className="no-patients">{t("list.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colName")}</th>
                        <th>{t("list.colCity")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr
                          key={contact?.id?.value || contact?.id}
                          className="table-item-link"
                          onClick={() => handleEditClick(contact)}
                          style={{ cursor: "pointer" }}>
                          <td>
                            <div className="table-item-name">
                              {contact?.name}
                            </div>
                          </td>
                          <td>{contact?.region}</td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={2} className="summary-cell">
                          {t("list.found", { count: contacts.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* === МОДАЛЬНОЕ ОКНО === */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("list.addTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.nameLabel")}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newCity.name}
                onChange={handleInputChange}
                placeholder={t("modal.namePlaceholder")}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.phoneLabel")}</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={newCity.phone}
                onChange={handleInputChange}
                placeholder="+7 (999) 123-45-67"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                name="email"
                value={newCity.email}
                onChange={handleInputChange}
                placeholder="example@mail.com"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fields.address")}</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={newCity.address}
                onChange={handleInputChange}
                placeholder={t("modal.addressPlaceholder")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fields.time")}</Form.Label>
              <Form.Control
                type="text"
                name="time"
                value={newCity.time}
                onChange={handleInputChange}
                placeholder={t("modal.timePlaceholder")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("fields.city")}</Form.Label>
            <Form.Select
              name="region"
              value={newCity.region}
              onChange={handleInputChange}
            >
              <option value="">{t("modal.selectCity")}</option>
              {Object.entries(citiesMap).map(([cityName, cityId]) => (
                <option key={cityId} value={cityId}>
                  {cityName}
                </option>
              ))}
            </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("modal.descriptionLabel")}</Form.Label>
              <Form.Control
                as="textarea"
                type="text"
                name="description"
                rows={3}
                value={newCity.description}
                onChange={handleInputChange}
                placeholder={t("modal.descriptionPlaceholder")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleModalClose}>
            {t("actions.cancel", { ns: "common" })}
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={handleSaveCity}
            disabled={!newCity.name.trim()}>
            {t("actions.save", { ns: "common" })}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <EditableModal
        show={showEditModal}
        onHide={handleEditModalClose}
        title={t("list.editTitle")}
        initialData={clinicInitialData}
        fields={clinicFields}
        onSave={handleSaveClinic}
      />
    </Container>
  );
}

export default Contacts;
